import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

type ToastKind = "success" | "error" | "info";
type Toast = { id: number; kind: ToastKind; text: string };

type ToastContextValue = {
  show: (text: string, kind?: ToastKind) => void;
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t: translate } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (text: string, kind: ToastKind = "info") => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, kind, text }]);
      setTimeout(() => dismiss(id), kind === "error" ? 6000 : 4000);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (t) => show(t, "success"),
      error: (t) => show(t, "error"),
      info: (t) => show(t, "info"),
    }),
    [show],
  );

  const dismissLabel = translate("toast.dismissLabel");
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div style={stack} aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          // Click-anywhere-to-dismiss, so the toast itself is the button. The
          // surrounding stack is the live region that announces new toasts.
          <button
            key={t.id}
            type="button"
            aria-label={`${t.text} — ${dismissLabel}`}
            style={{ ...toast, ...palette[t.kind] }}
            onClick={() => dismiss(t.id)}
            title={dismissLabel}
          >
            {t.text}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

/**
 * Convenience: take an unknown error and show a toast. Knows about ApiError shape.
 */
export function useToastError() {
  const { error } = useToast();
  const { t } = useTranslation();
  const defaultFallback = t("toast.defaultError");
  return useCallback(
    (err: unknown, fallback?: string) => {
      const e = err as { message?: string };
      error(e?.message || fallback || defaultFallback);
    },
    [error, defaultFallback],
  );
}

const stack: React.CSSProperties = {
  position: "fixed",
  bottom: 24,
  right: 24,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  zIndex: 9999,
  pointerEvents: "none",
};
const toast: React.CSSProperties = {
  padding: "12px 16px",
  border: 0,
  textAlign: "left",
  fontFamily: "inherit",
  borderRadius: 8,
  fontSize: 13,
  boxShadow: "0 8px 24px rgba(0,0,0,.18)",
  cursor: "pointer",
  pointerEvents: "auto",
  maxWidth: 360,
  color: "#fff",
};
const palette: Record<ToastKind, React.CSSProperties> = {
  success: { background: "#15803d" },
  error:   { background: "#b91c1c" },
  info:    { background: "#0b3a6f" },
};
